// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import {
  Dropdown,
  initializeIcons,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from '@fluentui/react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TopNav from '../components/TopNav';

initializeIcons();

type InterviewResponse = {
  reservationNumber: string;
  candidateName: string;
  candidateEmail: string;
  role: string;
  expiresAt: string;
  interviewUrl: string;
};

type RoleRecord = {
  id: number;
  name: string;
  systemPrompt: string;
  createdAt: string;
};

type AdminProps = {
  userEmail: string;
};

const Admin = ({ userEmail }: AdminProps): JSX.Element => {
  const location = useLocation();
  const authProvider = useMemo(() => (location.pathname.startsWith('/app/v2') ? 'login' : 'sso'), [location.pathname]);
  const interviewPath = authProvider === 'login' ? '/app/v2/interview' : '/app/v1/interview';

  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdInterview, setCreatedInterview] = useState<InterviewResponse | null>(null);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');

  useEffect(() => {
    const fetchRoles = async (): Promise<void> => {
      setRolesLoading(true);
      setRolesError(null);
      try {
        const response = await fetch('/api/roles', { credentials: 'include' });
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('auth-unauthorized'));
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to load roles.');
        }
        const data = (await response.json()) as { roles: RoleRecord[] };
        setRoles(Array.isArray(data.roles) ? data.roles : []);
      } catch (error) {
        setRolesError('Unable to load roles. Please refresh and try again.');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setFormError(null);
    setCreatedInterview(null);

    if (!candidateName.trim() || !candidateEmail.trim() || !expiresAt.trim() || !selectedRole.trim()) {
      setFormError('Please enter the candidate name, email, role, and expiry date.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/admin/interviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          candidateName,
          candidateEmail,
          role: selectedRole,
          expiresAt,
          interviewPath
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        setFormError(errorBody?.error || 'Unable to create interview.');
        return;
      }

      const data = (await response.json()) as InterviewResponse;
      setCreatedInterview(data);
      setCandidateName('');
      setCandidateEmail('');
      setExpiresAt('');
      setSelectedRole('');
    } catch (error) {
      setFormError('Unable to create interview. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: 32, maxWidth: 720, margin: '0 auto' } }}>
      <TopNav isAuthenticated={true} isAdmin={true} />
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Text variant="xLarge">Interview Admin</Text>
          {userEmail && <Text variant="small">Signed in as {userEmail}</Text>}
        </Stack>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack tokens={{ childrenGap: 16 }}>
          <TextField
            label="Candidate name"
            value={candidateName}
            onChange={(_, value) => setCandidateName(value || '')}
            required
          />
          <TextField
            label="Candidate email"
            type="email"
            value={candidateEmail}
            onChange={(_, value) => setCandidateEmail(value || '')}
            required
          />
          <TextField
            label="Expiry date"
            type="datetime-local"
            value={expiresAt}
            onChange={(_, value) => setExpiresAt(value || '')}
            required
            placeholder="YYYY-MM-DDThh:mm"
          />
          <Dropdown
            label="Role"
            placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
            options={roles.map((role) => ({ key: role.name, text: role.name }))}
            selectedKey={selectedRole || undefined}
            onChange={(_, option) => setSelectedRole(String(option?.key || ''))}
            disabled={rolesLoading || roles.length === 0}
            required
          />
          {rolesError && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              {rolesError}
            </MessageBar>
          )}
          {formError && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              {formError}
            </MessageBar>
          )}
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create interview'}
          </PrimaryButton>
        </Stack>
      </form>

      {createdInterview && (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 24 } }}>
          <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
            Interview created and email sent.
          </MessageBar>
          <Text>
            Reservation #: <strong>{createdInterview.reservationNumber}</strong>
          </Text>
          <Text>
            Candidate: {createdInterview.candidateName} ({createdInterview.candidateEmail})
          </Text>
          <Text>Expiry: {new Date(createdInterview.expiresAt).toLocaleString()}</Text>
          <Text>
            Interview URL: <a href={createdInterview.interviewUrl}>{createdInterview.interviewUrl}</a>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export default Admin;
